import { useSyncExternalStore } from 'react';

// Dev-only stand-in for a resident session, for the `engage_mock` organization: pages there have no
// Descope session to read. A page that never chose is signed in, so the mock organization shows its
// signed-in pages by default; `?session=out` starts a page signed out, and the mock sign-in or
// sign-out flips it.
// The choice lives in sessionStorage, which is the source of truth: it is read on every access, and
// once a choice has been made, a removed key (DevTools, "clear site data") reads as signed out.
const STORAGE_KEY = 'engage_mock_session';
const SIGNED_IN = 'in';
const SIGNED_OUT = 'out';

const listeners = new Set<() => void>();

// `undefined` when storage cannot be read, so the in-memory choice stands in for it.
const readStored = (): string | null | undefined => {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return undefined;
  }
};

const store = (value: string) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Storage is unavailable (private mode); the choice then lasts until the page is reloaded.
  }
};

let hasChosen = false;
let memorySignedIn = true;

const choose = (signedIn: boolean) => {
  hasChosen = true;
  memorySignedIn = signedIn;
  store(signedIn ? SIGNED_IN : SIGNED_OUT);
};

if (typeof window !== 'undefined') {
  const requested = new URLSearchParams(window.location.search).get('session');
  if (requested === SIGNED_IN || requested === SIGNED_OUT) choose(requested === SIGNED_IN);
  else {
    const stored = readStored();
    hasChosen = stored === SIGNED_IN || stored === SIGNED_OUT;
    memorySignedIn = stored !== SIGNED_OUT;
  }
}

export const getMockSignedIn = (): boolean => {
  const stored = readStored();
  if (stored === undefined) return memorySignedIn;
  if (stored === null) return !hasChosen;
  return stored !== SIGNED_OUT;
};

const notify = () => listeners.forEach((listener) => listener());

export const setMockSignedIn = (signedIn: boolean): void => {
  choose(signedIn);
  notify();
};

// DevTools edits to sessionStorage raise no event in the page, so the store looks again whenever the
// resident comes back to it.
export const subscribeMockSession = (listener: () => void): (() => void) => {
  if (listeners.size === 0) {
    window.addEventListener('focus', notify);
    document.addEventListener('visibilitychange', notify);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener('focus', notify);
      document.removeEventListener('visibilitychange', notify);
    }
  };
};

// The server always renders signed in, the default; the client then settles on the stored choice.
export const useMockSignedIn = (): boolean => useSyncExternalStore(subscribeMockSession, getMockSignedIn, () => true);
